import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { ProductionService } from '../services/production.service';
import * as ProductionActions from './productions.actions';

@Injectable()
export class ProductionsEffects {
    private _actions$ = inject(Actions);
    private _productionService = inject(ProductionService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all productions',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Productions
    getAllProductions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductionActions.loadProductions),
            switchMap(() => {
                return this._productionService.getAll().pipe(
                    map((res) => {
                        return ProductionActions.loadProductionsSuccess({
                            productions: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProductionActions.loadProductionsFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One Production
    getProduction$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductionActions.loadProduction),
            switchMap((action) => {
                return this._productionService.getOne(action.id).pipe(
                    map((res) => {
                        return ProductionActions.loadProductionSuccess({
                            production: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProductionActions.loadProductionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Production
    createProduction$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductionActions.createProduction),
            switchMap((action) => {
                return this._productionService.create(action.data).pipe(
                    map((res) => {
                        return ProductionActions.createProductionSuccess({
                            production: res.data,
                            success: 'Production created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProductionActions.createProductionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Production
    updateProduction$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductionActions.updateProduction),
            switchMap((action) => {
                return this._productionService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            return ProductionActions.updateProductionSuccess({
                                production: res.data,
                                success: 'Production updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                ProductionActions.updateProductionFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Production
    deleteProduction$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ProductionActions.deleteProduction),
            switchMap((action) => {
                return this._productionService.delete(action.id).pipe(
                    map(() => {
                        return ProductionActions.deleteProductionSuccess({
                            id: action.id,
                            success: 'Production deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ProductionActions.deleteProductionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onProductionActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ProductionActions.createProductionSuccess,
                    ProductionActions.updateProductionSuccess,
                    ProductionActions.deleteProductionSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onProductionActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ProductionActions.loadProductionsFailure,
                    ProductionActions.loadProductionFailure,
                    ProductionActions.createProductionFailure,
                    ProductionActions.updateProductionFailure,
                    ProductionActions.deleteProductionFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
